import React, { useState, useEffect } from 'react'
import { Alert, Button, Card, CardBody, Spinner } from 'reactstrap'
import { useQuery } from 'react-query'
import {
  supportGetReservePlan,
  supportCreateReservePlan,
  supportUpdateReservePlan,
  supportDeleteReservePlan,
  supportGetPaymentReserves,
} from '../../../helpers/lopay_api_helper'
import {
  ReservePlanDto,
  CreateReservePlanRequest,
  UpdateReservePlanRequest,
} from '../../../api-dtos/support/merchant/reserve-plan.dto.interface'
import { GetPaymentReservesParams } from '../../../api-dtos/support/merchant/payment-reserve.dto.interface'
import {
  ReservePlanCard,
  PaymentReservesTable,
  ReservePlanModals,
} from './components'

interface RiskReservesProps {
  merchantId: string
  isActive: boolean
}

const RiskReserves: React.FC<RiskReservesProps> = ({
  merchantId,
  isActive,
}) => {
  const [reservePlan, setReservePlan] = useState<ReservePlanDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [formData, setFormData] = useState<CreateReservePlanRequest>({
    percent: 5,
    minimumDays: 14,
  })
  const [submitting, setSubmitting] = useState(false)

  const [tablePage, setTablePage] = useState(1)
  const [tableSizePerPage, setTableSizePerPage] = useState(10)

  const paymentReservesQuery = useQuery(
    ['paymentReserves', merchantId, tablePage, tableSizePerPage, isActive],
    async () => {
      if (!isActive) return { data: [], hasMore: false, total: 0 }

      const params: GetPaymentReservesParams = {
        limit: tableSizePerPage,
        offset: (tablePage - 1) * tableSizePerPage,
      }
      return await supportGetPaymentReserves(merchantId, params)
    },
    {
      enabled: isActive,
      keepPreviousData: true,
    },
  )

  useEffect(() => {
    const loadReservePlan = async () => {
      try {
        setLoading(true)
        const plan = await supportGetReservePlan(merchantId)
        setReservePlan(plan)
      } catch (err) {
        setError('Failed to load reserve plan')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    if (isActive) {
      loadReservePlan()
    }
  }, [isActive, merchantId])

  const handleCreatePlan = async () => {
    try {
      setSubmitting(true)
      const newPlan = await supportCreateReservePlan(merchantId, formData)
      setReservePlan(newPlan)
      setShowCreateModal(false)
      setFormData({ percent: 5, minimumDays: 14 })
      paymentReservesQuery.refetch()
    } catch (err: any) {
      setError(err.message || 'Failed to create reserve plan')
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdatePlan = async () => {
    if (!reservePlan) return
    try {
      setSubmitting(true)
      const updateData: UpdateReservePlanRequest = {
        percent: formData.percent,
        minimumDays: formData.minimumDays,
      }
      const updatedPlan = await supportUpdateReservePlan(
        merchantId,
        reservePlan.id,
        updateData,
      )
      setReservePlan(updatedPlan)
      setShowEditModal(false)
      paymentReservesQuery.refetch()
    } catch (err: any) {
      setError(err.message || 'Failed to update reserve plan')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeletePlan = async () => {
    if (!reservePlan) return
    try {
      setSubmitting(true)
      await supportDeleteReservePlan(merchantId, reservePlan.id)
      setReservePlan(null)
      setShowDeleteModal(false)
      paymentReservesQuery.refetch()
    } catch (err: any) {
      setError(err.message || 'Failed to delete reserve plan')
    } finally {
      setSubmitting(false)
    }
  }

  const openEditModal = () => {
    if (reservePlan) {
      setFormData({
        percent: reservePlan.percent,
        minimumDays: reservePlan.minimumDays,
      })
      setShowEditModal(true)
    }
  }

  const handleTableChange = (page: number, sizePerPage: number) => {
    setTablePage(page)
    setTableSizePerPage(sizePerPage)
  }

  if (loading) {
    return (
      <Card>
        <CardBody className="text-center">
          <Spinner />
          <p className="mt-2">Loading reserve information...</p>
        </CardBody>
      </Card>
    )
  }

  return (
    <div>
      {error && (
        <Alert color="danger" className="mb-4">
          {error}
          <Button
            color="link"
            size="sm"
            onClick={() => setError(null)}
            className="float-end"
          >
            ×
          </Button>
        </Alert>
      )}

      <ReservePlanCard
        reservePlan={reservePlan}
        onEditPlan={openEditModal}
        onDeletePlan={() => setShowDeleteModal(true)}
        onCreatePlan={() => setShowCreateModal(true)}
      />

      <PaymentReservesTable
        paymentReservesQuery={paymentReservesQuery}
        reservePlan={reservePlan}
        tablePage={tablePage}
        tableSizePerPage={tableSizePerPage}
        onTableChange={handleTableChange}
      />

      <ReservePlanModals
        showCreateModal={showCreateModal}
        showEditModal={showEditModal}
        showDeleteModal={showDeleteModal}
        formData={formData}
        submitting={submitting}
        onCloseCreateModal={() => setShowCreateModal(false)}
        onCloseEditModal={() => setShowEditModal(false)}
        onCloseDeleteModal={() => setShowDeleteModal(false)}
        onFormDataChange={setFormData}
        onCreatePlan={handleCreatePlan}
        onUpdatePlan={handleUpdatePlan}
        onDeletePlan={handleDeletePlan}
      />
    </div>
  )
}

export default RiskReserves
